import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';

const SpotifySearch = ({ deviceId, isPremium, onTrackSelect }) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [calmingPlaylists, setCalmingPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('playlists'); // 'playlists' or 'search'
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);

  // Load curated calming playlists on mount
  useEffect(() => {
    loadCalmingPlaylists();
  }, []);

  const loadCalmingPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/spotify/calming-playlists', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalmingPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Failed to load playlists:', error);
    }
  };

  const searchTracks = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/spotify/search?q=${encodeURIComponent(query)}&search_type=track&limit=20`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setSearchResults(response.data.tracks?.items || []);
      setActiveTab('search');
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search tracks');
    } finally {
      setLoading(false);
    }
  };

  const playTrack = async (track) => {
    if (!isPremium) {
      // Open in Spotify app for free users
      window.open(track.external_urls.spotify, '_blank');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/spotify/play',
        {
          track_uri: track.uri,
          position_ms: 0,
          device_id: deviceId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setIsPlaying(true);
      setCurrentTrack(track);
      onTrackSelect && onTrackSelect(track);
      toast.success(`Now playing: ${track.name}`);
    } catch (error) {
      console.error('Play error:', error);
      toast.error('Failed to play track. Try opening Spotify app first.');
    }
  };

  const pausePlayback = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        '/api/spotify/pause',
        { device_id: deviceId },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setIsPlaying(false);
      toast.success('Playback paused');
    } catch (error) {
      console.error('Pause error:', error);
      toast.error('Failed to pause');
    }
  };

  const openPlaylist = (playlist) => {
    window.open(playlist.external_urls.spotify, '_blank');
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <Card className="glass-card p-4">
        <div className="flex space-x-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && searchTracks()}
            placeholder="Search for calming music, meditation, nature sounds..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
          <Button
            onClick={searchTracks}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? '🔍 Searching...' : '🔍 Search'}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex space-x-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('playlists')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'playlists'
              ? 'text-blue-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎧 Curated Playlists
        </button>
        <button
          onClick={() => setActiveTab('search')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'search'
              ? 'text-blue-600 border-b-2 border-emerald-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🔍 Search Results
        </button>
      </div>

      {/* Current Playing Track */}
      {currentTrack && isPremium && (
        <Card className="glass-card p-4 bg-gradient-to-r from-blue-50 to-amber-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {currentTrack.album?.images?.[2] && (
                <img
                  src={currentTrack.album.images[2].url}
                  alt={currentTrack.name}
                  className="w-12 h-12 rounded"
                />
              )}
              <div>
                <p className="font-medium text-gray-900">{currentTrack.name}</p>
                <p className="text-xs text-gray-600">
                  {currentTrack.artists?.map(a => a.name).join(', ')}
                </p>
              </div>
            </div>
            <Button
              onClick={pausePlayback}
              variant="outline"
              size="sm"
            >
              {isPlaying ? '⏸️ Pause' : '▶️ Resume'}
            </Button>
          </div>
        </Card>
      )}

      {/* Curated Playlists Tab */}
      {activeTab === 'playlists' && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {calmingPlaylists.length === 0 ? (
            <Card className="col-span-full glass-card p-6 text-center text-gray-500">
              Loading calming playlists...
            </Card>
          ) : (
            calmingPlaylists.map((playlist) => (
              <Card key={playlist.id} className="glass-card p-4 hover:shadow-lg transition-shadow">
                {playlist.images?.[0] && (
                  <img
                    src={playlist.images[0].url}
                    alt={playlist.name}
                    className="w-full h-40 object-cover rounded-lg mb-3"
                  />
                )}
                <h3 className="font-semibold text-gray-900 mb-1 truncate">
                  {playlist.name}
                </h3>
                <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                  {playlist.description || 'Spotify Playlist'}
                </p>
                <Badge variant="outline" className="text-xs mb-3">
                  {playlist.tracks?.total || 0} tracks
                </Badge>
                <Button
                  onClick={() => openPlaylist(playlist)}
                  className="w-full btn-secondary"
                  size="sm"
                >
                  Open in Spotify
                </Button>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Search Results Tab */}
      {activeTab === 'search' && (
        <div className="space-y-2">
          {searchResults.length === 0 ? (
            <Card className="glass-card p-6 text-center text-gray-500">
              Search for calming music, meditation tracks, or nature sounds
            </Card>
          ) : (
            searchResults.map((track) => (
              <Card key={track.id} className="glass-card p-3 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    {track.album?.images?.[2] && (
                      <img
                        src={track.album.images[2].url}
                        alt={track.name}
                        className="w-12 h-12 rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">
                        {track.name}
                      </h4>
                      <p className="text-xs text-gray-600 truncate">
                        {track.artists?.map(a => a.name).join(', ')}
                      </p>
                      <p className="text-xs text-gray-400">
                        {Math.floor(track.duration_ms / 60000)}:{String(Math.floor((track.duration_ms % 60000) / 1000)).padStart(2, '0')}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => playTrack(track)}
                    size="sm"
                    className="btn-primary"
                  >
                    {isPremium ? '▶️ Play' : '🎵 Open'}
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SpotifySearch;
